import { useState, useRef, useEffect } from "react";
import { categories, mainSiteUrl } from "@/mocks/categories";
import { Link } from "react-router-dom";

const apparelCategories = ["Shoes", "Hoodies / Sweaters", "T-Shirts", "Jackets", "Pants / Shorts", "Sets", "Underwear / Underpants", "Jersey"];
const accessoryCategories = ["Headwear", "Accessories", "Bags", "Belts", "Perfumes"];

export default function Navbar() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const renderCategoryItem = (cat: typeof categories[number]) => (
    <a
      key={cat.name}
      href={cat.url}
      target="_blank"
      rel="noopener nofollow"
      className="flex items-center gap-3 px-4 py-3 hover:bg-background-100/80 rounded-xl transition-colors cursor-pointer"
    >
      <div className="w-9 h-9 rounded-xl bg-secondary-100 flex items-center justify-center flex-shrink-0">
        <i className={`${cat.icon} text-secondary-600 text-sm`}></i>
      </div>
      <div className="min-w-0 flex-1">
        <span className="text-sm font-medium text-foreground-800 block truncate">{cat.name}</span>
        <span className="text-xs text-foreground-400 line-clamp-1">{cat.description}</span>
      </div>
      <i className="ri-external-link-line text-foreground-300 text-xs flex-shrink-0"></i>
    </a>
  );

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-background-50/95 backdrop-blur-md shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-18">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group cursor-pointer">
            <div className="w-9 h-9 rounded-lg bg-primary-500 flex items-center justify-center">
              <i className="ri-file-list-3-line text-background-50 text-lg"></i>
            </div>
            <span className="font-heading text-lg md:text-xl font-bold text-foreground-900 whitespace-nowrap">
              Hipo<span className="text-primary-500">Spread</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-6">
            {/* Dropdown */}
            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium text-foreground-700 hover:text-foreground-900 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                <span>Browse Categories</span>
                <i
                  className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${
                    isDropdownOpen ? "rotate-180" : ""
                  }`}
                ></i>
              </button>

              {isDropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-72 bg-background-50 rounded-xl shadow-lg border border-background-200/70 py-2 max-h-96 overflow-y-auto z-50">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-background-100 transition-colors cursor-pointer"
                    >
                      <div className="w-8 h-8 rounded-lg bg-secondary-100 flex items-center justify-center flex-shrink-0">
                        <i className={`${cat.icon} text-secondary-600 text-sm`}></i>
                      </div>
                      <span className="text-sm text-foreground-800 whitespace-nowrap">{cat.name}</span>
                      <i className="ri-external-link-line text-foreground-400 text-xs ml-auto"></i>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/hipobuy-spreadsheet-guide"
              className="text-sm font-medium text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap cursor-pointer"
            >
              Spreadsheet Guide
            </Link>

            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="text-sm font-medium text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap cursor-pointer"
            >
              Hipobuy Sheet
            </a>
          </div>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2 rounded-full text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              Buy It Now
            </a>
          </div>

          {/* Mobile Hamburger */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
          >
            <i className={`${isMobileMenuOpen ? "ri-close-line" : "ri-menu-line"} text-xl`}></i>
          </button>
        </div>

        {/* Mobile Menu — Enhanced */}
        {isMobileMenuOpen && (
          <div className="md:hidden absolute left-0 right-0 top-full bg-background-50 shadow-xl border-t border-background-200/70 pb-6 z-50 animate-in fade-in slide-in-from-top-2 max-h-[calc(100vh-64px)] overflow-y-auto overscroll-contain">
            {/* Main Links */}
            <div className="px-4 py-3 space-y-1">
              <span className="text-[11px] font-bold text-foreground-400 uppercase tracking-wider block mb-2">Navigate</span>
              <Link
                to="/hipobuy-spreadsheet-guide"
                onClick={() => setIsMobileMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-foreground-700 hover:bg-background-100 rounded-xl cursor-pointer"
              >
                <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center">
                  <i className="ri-article-line text-primary-600 text-sm"></i>
                </div>
                <span>Spreadsheet Guide</span>
              </Link>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-foreground-700 hover:bg-background-100 rounded-xl cursor-pointer"
              >
                <div className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center">
                  <i className="ri-shopping-bag-3-line text-accent-600 text-sm"></i>
                </div>
                <span>Hipobuy Sheet</span>
              </a>
            </div>

            {/* Platform Links */}
            <div className="px-4 py-3 space-y-1">
              <span className="text-[11px] font-bold text-foreground-400 uppercase tracking-wider block mb-2">Spreadsheet Platforms</span>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { name: "Hipobuy", slug: "/hipobuy-spreadsheet", color: "primary" },
                  { name: "Kakobuy", slug: "/kakobuy-spreadsheet", color: "secondary" },
                  { name: "CSSBuy", slug: "/cssbuy-spreadsheet", color: "accent" },
                  { name: "Superbuy", slug: "/superbuy-spreadsheet", color: "primary" },
                  { name: "Oopbuy", slug: "/oopbuy-spreadsheet", color: "secondary" },
                ].map((p) => (
                  <Link
                    key={p.slug}
                    to={p.slug}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`px-3 py-2.5 rounded-xl text-xs font-semibold text-${p.color}-700 bg-${p.color}-50 hover:bg-${p.color}-100 transition-colors cursor-pointer text-center whitespace-nowrap`}
                  >
                    {p.name}
                  </Link>
                ))}
              </div>
            </div>

            {/* Apparel Categories */}
            <div className="px-4 py-3">
              <span className="text-[11px] font-bold text-foreground-400 uppercase tracking-wider block mb-2">Apparel & Footwear</span>
              <div className="space-y-1">
                {categories.filter(c => apparelCategories.includes(c.name)).map(renderCategoryItem)}
              </div>
            </div>

            {/* Accessories Categories */}
            <div className="px-4 py-3">
              <span className="text-[11px] font-bold text-foreground-400 uppercase tracking-wider block mb-2">Accessories & Lifestyle</span>
              <div className="space-y-1">
                {categories.filter(c => accessoryCategories.includes(c.name)).map(renderCategoryItem)}
              </div>
            </div>

            {/* Other Stuff */}
            <div className="px-4 py-3">
              <span className="text-[11px] font-bold text-foreground-400 uppercase tracking-wider block mb-2">More</span>
              <div className="space-y-1">
                {categories.filter(c => ![...apparelCategories, ...accessoryCategories].includes(c.name)).map(renderCategoryItem)}
              </div>
            </div>

            {/* CTA */}
            <div className="px-4 pt-4">
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="block w-full text-center px-4 py-3.5 rounded-full text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                Buy It Now
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}